import { Component, ElementRef, OnInit, ViewChild, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, map, of, skip, startWith, switchMap, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { helper } from 'src/environments/helper';
import { ProiecteService } from '../proiecte.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { CommonService } from 'src/app/services/common.service';
import { IPlace, IProiectAddPayload, IProiectForm } from '../proiecte.interface';
import { environment } from 'src/environments/environment';

import { MapsService } from 'src/app/services/maps.service';
import { dateRangeValidator } from 'src/app/validators';


@Component({
    selector: 'app-add-proiecte',
    templateUrl: './add-proiecte.component.html',
    styleUrls: ['./add-proiecte.component.scss']
})
export class AddProiecteComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;
    helper = helper;

    isLoading = false;
    uploading = false;
    searchedTags: Generic[] = [];
    tagChangesSubscription = new Subscription();
    searchedPlaces: IPlace[] = [];
    placeData!: any;

    // Observables
    directiiActive$!: Observable<Generic[]>;
    compartimenteActive$!: Observable<Generic[]>;
    sectiuni$!: Observable<Generic[]>;
    categoriiActive$!: Observable<Generic[]>;
    furnizoriActive$!: Observable<Generic[]>;

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    // ==================================
    // google maps zone - START
    defaultMapCoords = { lat: 44.4277119, lng: 26.0994927 }

    draggableMarker: any;
    infoWindow: any;
    private addressNameSubscription: Subscription = new Subscription();

    async initMap() {
        // librariile necesare pentru randare harta, creare pin draggable si init infoWindow.
        const { Map, InfoWindow } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const map = new Map(document.getElementById('map') as HTMLElement, {
            center: this.defaultMapCoords,
            zoom: 14,
            mapId: '4504f8b37365c3d0',
        });
        map.addListener('click', (event: any) => {
            const latLngJson = event.latLng?.toJSON();
            this.repositionPin(latLngJson)
            this.reverseGeodecoding(latLngJson);
        });

        this.infoWindow = new InfoWindow();

        this.draggableMarker = new AdvancedMarkerElement({
            map,
            position: this.defaultMapCoords,
            gmpDraggable: true,
            title: "Centrati pin-ul pe locatia proiectului. Click pe harta sau trageti de pin.",
        });
        this.draggableMarker.addListener('dragend', (event: any) => {
            const position = this.draggableMarker.position as google.maps.LatLng;
            //this.showMarkerInfo(position);
            this.reverseGeodecoding(position);
        });
    }

    // PIN-ul se repozitioneaza la:
    // click pe harta
    // la selectie locatie proiect din dropdown
    repositionPin(position: any, address: any = null) {
        this.draggableMarker.position = position;
        this.draggableMarker.map.panTo(position); // centreaza harta cu animatie
        //this.draggableMarker.map.setCenter(latLon); // centreaza harta brusc
        const markerInfo = `Pin setat la: ${position.lat}, ${position.lng}.\nPuteți mișca pin-ul la locația exactă a proiectului dând click pe hartă sau trăgând de pin (drag&drop).`;
        this.draggableMarker.title = markerInfo;
        //this.showMarkerInfo(position); // afiseaza un popup pe marker cu coordonatele
    }

    // la click pe harta sau la drag&drop pin afisat, se face reverse geodecoding - din coordonate => adresa (locatia proiectului)
    reverseGeodecoding(position: any) {
        let geocoder = new google.maps.Geocoder();
        let latlng = new google.maps.LatLng(position.lat, position.lng);

        let request = {
            location: latlng
        };

        geocoder.geocode(request, (results, status) => {
            if (status == google.maps.GeocoderStatus.OK) {
                if (results && results[0] != null) {
                    const placeId = results[0].place_id
                    const address = results[0].formatted_address;
                    this.searchedPlaces.push({ id: placeId, locatie: address })
                    this.toggleAddressNameSubscription(false);
                    this.form.controls['addressName'].setValue(address);
                    this.form.controls['lat'].setValue(position.lat);
                    this.form.controls['lng'].setValue(position.lng);
                    this.toggleAddressNameSubscription(true);
                } else {
                    this.showMarkerInfo(position, "Adresa necunoscută.");
                }
            }
            else {
                console.warn('reverse geodecoding status : ', status);
                this.showMarkerInfo(position, "Adresa nu a putut fi decodată pe baza coordonatelor alese de pe hartă.");
            }
        })
    }

    showMarkerInfo(position: any, content: null | string = null) {
        this.infoWindow.close();
        this.infoWindow.setContent(content ?? `Pin setat la: ${position.lat}, ${position.lng}`);
        this.infoWindow.open(this.draggableMarker.map, this.draggableMarker);
    }

    onPlaceSelected(option: { id: string; locatie: string }) {
        this.isLoading = true;
        this.mapsService.getPlaceData(option.id)
            .pipe(
                map((results: any) => results.data),
                finalize(() => (
                    this.isLoading = false
                ))
            ).subscribe({
                next: (results: any) => {
                    this.placeData = results;
                    this.form.controls['lat'].setValue(this.placeData?.geometry?.location.lat);
                    this.form.controls['lng'].setValue(this.placeData?.geometry?.location.lng);
                    this.repositionPin(this.placeData.geometry.location)
                },
                error: (error: any) => {
                    console.error('Error loading the place data', error);
                },
            });
    }

    async ngAfterViewInit() {
        await this.initMap();

        // when addressName changes, we search for the places
        this.toggleAddressNameSubscription(true);
    }

    toggleAddressNameSubscription(subscribe: boolean) {
        if (subscribe) {
            this.addressNameSubscription = this.form.controls['addressName'].valueChanges
                .pipe(
                    debounceTime(1000),
                    distinctUntilChanged()
                )
                .subscribe((searchTerm: string) => {
                    this.isLoading = true;
                    this.mapsService.findPlaces({ input: searchTerm })
                        .subscribe((response: any) => {
                            this.isLoading = false;
                            this.searchedPlaces = response.data;
                        });
                });
        }
        else {
            if (this.addressNameSubscription) {
                this.addressNameSubscription.unsubscribe();
            }
        }
    }

    // ==================================
    // google maps zone - END

    provider_type: { key: string; value: number }[] = [
        { key: '--Alegeți--', value: -1 },
        { key: 'Firmă', value: 1 },
        { key: 'Instituție Publică (Regie Propie)', value: 2 },
        { key: 'Firmă și Instituție Publică', value: 3 },
    ];

    project_type: { key: string; value: string | number }[] = [
        { key: '--Alegeți--', value: -1 },
        { key: 'Mic', value: 'Mic' },
        { key: 'Mare', value: 'Mare' },
    ];

    form: FormGroup = this.fb.group({
        subject: new FormControl('', [Validators.required]),
        scale: new FormControl(-1, [Validators.required, Validators.min(0)]),
        tag: new FormControl('', [Validators.required]),
        id_tag: new FormControl('', [Validators.required]),

        id_directie: new FormControl(-1, [Validators.required, Validators.min(0)]),
        id_compartiment: new FormControl(-1, []),
        id_sectiune: new FormControl(-1, [Validators.required, Validators.min(0)]),
        id_categorie: new FormControl(-1, []),
        tip_furnizor: new FormControl(-1, [Validators.required, Validators.min(0)]),
        id_provider: new FormControl(-1, [Validators.required, Validators.min(0)]),

        data_inceput: new FormControl('', [Validators.required]),    // "2024-03-08T13:06:55.920Z"
        data_finalizare: new FormControl('', [Validators.required]), // "2024-03-29T13:06:55.920Z"

        addressName: new FormControl('', [Validators.required]),
        lat: new FormControl('', [Validators.required]),
        lng: new FormControl('', [Validators.required]),
        addressObs: new FormControl('', []),
        description: new FormControl('', [Validators.required]),   // "<p>Descriere<br/><p>"
        files: this.fb.array([]),
        // category_obs: "",
        // lucrare_singulara: true,
    }, { validator: dateRangeValidator('data_inceput', 'data_finalizare') });

    constructor(
        private dialogRef: MatDialogRef<AddProiecteComponent>,
        private proiecteService: ProiecteService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private commonService: CommonService,
        private mapsService: MapsService,
        private fb: FormBuilder,
    ) { }

    ngOnInit(): void {
        this.loadDirectiiActive();
        this.loadSectiuni();
        this.renderTagSearch();
    }

    ngOnDestroy(): void {
        this.tagChangesSubscription.unsubscribe();
        this.toggleAddressNameSubscription(false);
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    loadDirectiiActive() {
        this.isLoading = true;

        this.directiiActive$ = this.commonService.getDirectiiActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        )
    }

    onDirectieChange(directieId: number): void {
        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
        } else {
            this.compartimenteActive$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectie(directieId)
            : this.commonService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    loadSectiuni() {
        this.isLoading = true;
        this.sectiuni$ = this.commonService.getSectiuniProiecte().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    onSectiuneChange(sectiuneId: number): void {
        if (sectiuneId > 0) {
            this.loadCategoriiActive(sectiuneId);
        } else {
            this.categoriiActive$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }
    }

    loadCategoriiActive(sectiuneId?: number) {
        this.isLoading = true;
        let categoriiObservable$ = sectiuneId
            ? this.commonService.filterCategoriiProiecte(sectiuneId)
            : this.commonService.getCategoriiProiecte();

        this.categoriiActive$ = categoriiObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onTipFurnizoriChange(provider_type: number): void {
        if (provider_type > 0) {
            this.loadFurnizoriActive(provider_type);
        } else {
            this.furnizoriActive$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }
    }

    loadFurnizoriActive(provider_type?: number) {
        this.isLoading = true;
        let furnizoriObservable$ = provider_type
            ? this.commonService.filterProviderProiecte(provider_type)
            : this.commonService.getProviderProiecte();

        this.furnizoriActive$ = furnizoriObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    formatDate(inputDate: Date) {
        // Get the current hour
        let currentHour = inputDate.getHours();

        // Add 3 hours
        inputDate.setHours(currentHour + 3);

        return inputDate.toISOString().split('T')[0];
    }

    submitForm() {
        this.isLoading = true;

        const {
            subject,
            scale,
            tag,
            id_tag,
            id_directie,
            id_compartiment,
            id_sectiune,
            id_categorie,
            id_provider,
            data_inceput,
            data_finalizare,
            addressName,
            lat,
            lng,
            addressObs,
            description,
            files,
        } = this.form.value as IProiectForm;
        const payload: IProiectAddPayload = {
            subject,
            scale,
            tag,
            id_tag,
            id_directie,
            id_compartiment,
            id_sectiune,
            id_categorie: id_categorie > -1 ? id_categorie : null,
            id_provider,
            data_inceput,
            data_finalizare,
            dt_inceput: this.formatDate(data_inceput),
            dt_finalizare: this.formatDate(data_finalizare),
            description,
            files,

            addressData: {
                locatie: addressName,
                lat: lat,
                lng: lng,
                locatie_obs: addressObs,
            },

            // Mocked data ??
            id_department: -1,
            id_user_assigned: -1,
            category_obs: "",
            lucrare_singulara: true,
        };

        this.proiecteService
            .addProiect(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    addFile(event: any, key: 'file') {
        event.eventPreventDefault = true;
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            this.uploading = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.proiecteService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (
                            response.status_code == 200 &&
                            typeof response.data !== 'undefined'
                        ) {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.form.get(
                                'files'
                            ) as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading = false;
                            this.notificationService.warningSwal(
                                this.successTitle,
                                helper.dialogConfig.generalMessages.fileSuccessfullyUploaded,
                                this.successIcon,
                            );
                        }
                    } else {
                        let errorMessage =
                            environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                        this.isLoading = false;
                        this.uploading = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading = false;

                    let msg;
                    try {
                        // try to catch the message from server
                        msg = result.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
        }
    }

    removeFile(index: number) {
        const files = this.form.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    renderTagSearch() {
        this.tagChangesSubscription = this.form
            .controls['tag'].valueChanges.pipe(
                skip(1), // Ignore the first emitted value
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((tagValue) => {
                    // Reset Tag ID before search
                    this.form.controls['id_tag'].setValue('');

                    this.isLoading = true;
                    return this.commonService.typeheadTagProiecte({ name: tagValue });
                }),
                tap((data: Generic[]) => {
                    this.isLoading = false;
                    this.searchedTags = data;
                }),
                catchError(() => of([]))
            )
            .subscribe();
    }

    onTagSelected(option: { id: number; name: string }) {
        // Unsubscribe from valueChanges to avoid an unnecessary call to the BE when setting a value
        this.tagChangesSubscription.unsubscribe();

        this.form.controls['tag'].setValue(option.name);
        this.form.controls['id_tag'].setValue(option.id);

        // Resubscribe to valueChanges after setting the value
        this.renderTagSearch();
    }
}
